// src/utils/invoice/removedInvoiceIds.js
//
// Invoice ids this session has deleted (KAN-1121, RD-BILL-040). Ids only (strings), memory only,
// cleared at sign-out (clearPerUserDeviceState).
//
// Why it exists: after a draft is deleted the live `invoices` list and the year-unbounded
// Ready-to-bill snapshot (useReadyToBillData) drop it at DIFFERENT times. Until the snapshot
// refetches, its copy of the draft still "bills" the draft's visits, so Cobrar flashed "all caught up"
// before the rows came back. `deleteInvoice` marks the id the moment the server confirms the delete;
// every reader filters marked ids out of both sources. One place for every delete path.
import { useSyncExternalStore } from 'react';

const removed = new Set();
// KAN-1042: visit ids the removed drafts held. Ready-to-bill is server-filtered (unbilled only), so a deleted
// draft's visits are missing from the snapshot until it refetches; overlayLiveJobs re-adds them from this set.
const releasedJobIds = new Set();
const listeners = new Set();
let version = 0;

const emit = () => {
  version += 1;
  listeners.forEach((l) => l());
};

/** @param {Array|string} ids removed invoice ids @param {Array<string>} [jobIds] visit ids those invoices held */
export function markInvoicesRemoved(ids, jobIds = []) {
  let changed = false;
  (Array.isArray(jobIds) ? jobIds : []).forEach((j) => {
    if (j == null || j === '') return;
    const key = String(j);
    if (!releasedJobIds.has(key)) { releasedJobIds.add(key); changed = true; }
  });
  (Array.isArray(ids) ? ids : [ids]).forEach((id) => {
    if (id == null || id === '') return;
    const key = String(id);
    if (!removed.has(key)) { removed.add(key); changed = true; }
  });
  if (changed) emit();
}

export function isInvoiceRemoved(id) {
  return id != null && removed.has(String(id));
}

/** Visit ids held by drafts removed this session (memory only, cleared with the removed ids). */
export function getRemovedInvoiceJobIds() {
  return releasedJobIds;
}

export function clearRemovedInvoiceIds() {
  if (removed.size === 0 && releasedJobIds.size === 0) return;
  removed.clear();
  releasedJobIds.clear();
  emit();
}

export const subscribeRemovedInvoiceIds = (listener) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};
export const getRemovedInvoiceIdsVersion = () => version;

/** Re-renders the caller whenever an id is marked or the set is cleared; returns the version (use it as a memo dep). */
export function useRemovedInvoiceIdsVersion() {
  return useSyncExternalStore(subscribeRemovedInvoiceIds, getRemovedInvoiceIdsVersion, getRemovedInvoiceIdsVersion);
}

/** Drops removed invoices from a list; returns the SAME array when nothing is removed (null/non-array pass through). */
export function withoutRemovedInvoices(list) {
  if (!Array.isArray(list) || removed.size === 0) return list;
  return list.some((i) => isInvoiceRemoved(i?.id)) ? list.filter((i) => !isInvoiceRemoved(i?.id)) : list;
}
